import { defaultDataModels } from '@constants/initDataModels';
import { dateUtils } from '@helpers/dateUtils';
import { translateKey } from '@helpers/resolveTranslationsKey';
import { campaignsApiService } from '@repos/campaigns.api';
import { formGroupsService } from '@services/domain-factories/formGroups';
import { fsService } from '@services/firebase/firestore/firestore';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { PermissionsService } from '@services/permissions';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { shortId } from '@utils/shortid';
import { Audience, Campaign, Form, FormGroup, Member } from '@vincerosoft/assessfluence-types';
import { limit, where } from 'firebase/firestore';
import { StoreApi } from 'zustand';

export const reminderObject = (): Campaign['reminders'] => ({
  is_active: false,
  frequency: 'weekly' as 'weekly',
  interval: 1,
  day_of_week: 1,
  start_date: new Date().toISOString(),
  end_date: new Date(new Date().setMonth(new Date().getMonth() + 2)).toISOString(),
  max_reminders: 3,
  next_run_id: null as unknown as string,
  last_sent_at: null as unknown as string,
  sent_count: 0
});

const campaignWithLegacyFields = (campaign: Campaign) =>
  campaign as Campaign & {
    source_type?: 'form' | 'form-group';
    source_id?: string;
  };

export const isCampaignGroup = (campaign: Campaign) =>
  getCampaignSourceType(campaign) === 'form-group';

export const isCampaignForm = (campaign: Campaign) => getCampaignSourceType(campaign) === 'form';

export const getCampaignSourceId = (campaign: Campaign) =>
  campaign.resource?.id ?? campaignWithLegacyFields(campaign).source_id;

export const getCampaignSourceType = (campaign: Campaign) =>
  campaign.resource?.type ?? campaignWithLegacyFields(campaign).source_type;

class CampaignsFactory {
  private whereTrigger = where('trigger', '==', 'campaigns');

  async campaignsForEntityUser(
    _store: StoreApi<unknown> | 'local',
    member: Member,
    options: {
      fromDate?: Date;
      limit?: number;
    },
    onProgress: (campaigns: Campaign[]) => void
  ) {
    const { query, batchQuery } = this.getQueryForCampaigns(member);

    if (options?.fromDate) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query.push(where('created_at', '>=', options?.fromDate) as any);
    }
    if (options?.limit) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query.push(limit(options.limit) as any);
    }

    if (batchQuery) {
      const campaigns = await collectionsService.campaigns.getDocsInBatches<
        Campaign & { created_at: Date }
      >(batchQuery, query, onProgress);

      return campaigns.flat();
    }

    return collectionsService.campaigns.getDocs<Campaign & { created_at: Date }>(query);
  }

  async campaignsForAccountUser(
    entityIds: string[],
    options: {
      fromDate?: Date;
      limit?: number;
    },
    onProgress: (campaigns: Campaign[]) => void
  ) {
    const campaigns = (
      await collectionsService.campaigns.getDocsInBatches<Campaign>(
        ['entity_id', 'in', entityIds],
        [
          this.whereTrigger,
          ...(options?.fromDate ? [where('created_at', '>=', options?.fromDate)] : []),
          ...(options?.limit ? [limit(options.limit)] : [])
        ],
        onProgress
      )
    ).flat();

    return campaigns;
  }

  async getCampaigns(
    _store: StoreApi<unknown> | 'local',
    options: {
      fromDate?: Date;
      limit?: number;
    },
    onProgress: (campaigns: Campaign[]) => void
  ) {
    const member = appUserStore.getState().currentMember as Member;

    return this.campaignsForEntityUser(_store, member, options, onProgress);
  }

  async getCampaignCounts() {
    const member = appUserStore.getState().currentMember as Member;
    const { query, batchQuery } = this.getQueryForCampaigns(member);
    if (batchQuery) {
      return collectionsService.campaigns.getCountInBatches(batchQuery, query);
    }
    return collectionsService.campaigns.getCount(query);
  }

  async getCampaignsBySourceId(sourceId: string, entityId?: string) {
    const query = [where('resource.id', '==', sourceId)];
    if (entityId) {
      query.push(where('entity_id', '==', entityId));
    }
    return collectionsService.campaigns.getDocs(query);
  }

  async getActiveCampaignsCountsBySourceId(sourceId: string, entityId?: string) {
    const query = [where('resource.id', '==', sourceId), where('status', '==', 'active')];
    if (entityId) {
      query.push(where('entity_id', '==', entityId));
    }
    return collectionsService.campaigns.getCount(query);
  }

  async getCampaignsByEntityId(entityId: string) {
    return collectionsService.campaigns.getDocs([where('entity_id', '==', entityId)]);
  }

  private getQueryForCampaigns(member: Member) {
    const { entity_id, user_group_ids } = member || {};

    if (PermissionsService.canReadAll('campaign')) {
      return {
        query: [where('entity_id', '==', entity_id), this.whereTrigger]
      };
    } else if (PermissionsService.canReadGroup('campaign') && user_group_ids?.length) {
      return {
        batchQuery: ['user_group_id', 'in', user_group_ids] as Parameters<
          typeof fsService.getDocsInBatches
        >[1],
        query: [this.whereTrigger]
      };
    }

    return { query: [where('owned_by', 'in', [member.user_id, member.id]), this.whereTrigger] };
  }

  getCampaignsByIds = async (
    campaignIds: string[],
    onProgress?: (campaigns: Campaign[]) => void
  ) => {
    if (!campaignIds?.length) {
      return [];
    }
    const campaignBatches = await collectionsService.campaigns.getDocsInBatches(
      ['id', 'in', campaignIds],
      [this.whereTrigger],
      onProgress
    );
    return campaignBatches.flat();
  };

  getCampaignById = async (campaignId: string) => {
    return collectionsService.campaigns.getDoc(campaignId);
  };

  async generateCampaign(
    intent: 'workspace' | 'content',
    data:
      | {
          source: 'form-group';
          formGroup?: FormGroup;
        }
      | {
          source: 'form';
          form?: Form;
        },
    userGroup: string
  ) {
    const { currentMember, appUser } = appUserStore.getState();

    const source = data.source === 'form-group' ? data.formGroup : data.form;

    const campaignName = `${translateKey(
      source!.name
    )} - ${dateUtils.format(Date.now())} - ${appUser!.name}`;

    const mapFormGroupToAud = (formId: string): Audience => ({
      id: shortId(),
      form_id: formId,
      member_ids: [currentMember!.id] as string[],
      list_ids: null as unknown as string[],
      count: 1
    });

    let audience: Audience[] = [];

    if (data.source === 'form-group') {
      const linkedFormIds = formGroupsService.extractLinkedFormIds([data.formGroup!]);
      audience = linkedFormIds.map((id) => mapFormGroupToAud(id));
    } else {
      audience = [mapFormGroupToAud(source!.id)];
    }

    // I map the form data to the campaign object
    const campaignData: Campaign = defaultDataModels().campaign(
      currentMember!.id,
      currentMember!.entity_id!
    ) as Campaign;

    const modifiedData: Campaign = {
      ...campaignData,
      name: campaignName,
      resource: {
        id: source!.id,
        type: data.source
      },
      type: 'sendout',
      audience,
      status: 'active' as Campaign['status'],
      is_draft: false,
      closing_date: dateUtils.add(1, 'year').toDate(),
      audience_count: 1,
      trigger: intent === 'content' ? 'learning-center' : 'workspace',
      user_group_id: userGroup
    };

    await collectionsService.campaigns.addDoc(campaignData.id, modifiedData);

    const campaign = await campaignsApiService.launchCampaign(campaignData.id);
    return campaign.data;
  }
}

export const campaignsService = new CampaignsFactory();
